import { useCallback, useEffect, useMemo, useState, type FormEvent } from "react";

import { formatDate } from "../lib/format";
import {
  createCalendarDay,
  createKeyRate,
  deleteCalendarDay,
  deleteKeyRate,
  fetchCalendar,
  fetchKeyRates,
  formatHundredthsAsPercent,
  parsePercentToHundredths,
  updateKeyRate,
  DAY_TYPE_LABELS,
  type CalendarDay,
  type CalendarDayType,
  type KeyRate,
} from "../lib/references";

/**
 * Секции «Настроек» для справочников (E3): производственный календарь и ключевая ставка ЦБ.
 * Только администратор — сервер проверяет право `reference:manage` на каждом маршруте (матрица
 * 4.3), навигация и эта страница видны лишь ему (правило 5). Оформление — токены docs/07
 * (правило 9): даты `ДД.ММ.ГГГГ` (правило 8), ставка — «16,5 %», моноширинные значения.
 */

const btnPrimary =
  "rounded-[var(--radius-m)] bg-accent px-3 py-1.5 text-[13px] font-bold text-surface disabled:opacity-50";
const btnSecondary =
  "rounded-[var(--radius-m)] border border-border px-3 py-1.5 text-[13px] font-bold text-ink hover:bg-bg disabled:opacity-50";
const inputCls =
  "w-full rounded-[var(--radius-m)] border border-border bg-surface px-2.5 py-1.5 text-[14px]";

type Load<T> =
  { kind: "loading" } | { kind: "ready"; items: T[] } | { kind: "error"; message: string };

/** Штамп типа дня календаря: нерабочий — приглушённый, рабочий выходной — акцент. */
function DayTypeStamp({ type }: { type: CalendarDayType }) {
  return type === "working" ? (
    <span className="stamp text-accent bg-control-bg">{DAY_TYPE_LABELS.working}</span>
  ) : (
    <span className="stamp text-waiting-fg bg-waiting-bg">{DAY_TYPE_LABELS.holiday}</span>
  );
}

// ── Производственный календарь ──────────────────────────────────────────────────

export function CalendarSection() {
  const [state, setState] = useState<Load<CalendarDay>>({ kind: "loading" });
  const [year, setYear] = useState<string>("");

  const load = useCallback(async () => {
    setState({ kind: "loading" });
    const res = await fetchCalendar();
    setState(
      res.ok ? { kind: "ready", items: res.value } : { kind: "error", message: res.message },
    );
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const items = useMemo(() => (state.kind === "ready" ? state.items : []), [state]);
  const years = useMemo(
    () => [...new Set(items.map((d) => d.date.slice(0, 4)))].sort().reverse(),
    [items],
  );
  // По умолчанию — самый свежий год из данных (год приёмки/эксплуатации сверху).
  const activeYear = year || years[0] || "";
  const shown = items.filter((d) => d.date.startsWith(activeYear));

  return (
    <section aria-labelledby="cal-title" className="mt-10">
      <h2 id="cal-title" className="mb-1 text-[16px] font-bold text-ink">
        Производственный календарь
      </h2>
      <p className="mb-3 text-[13px] text-muted">
        Нерабочие дни (праздники и переносы) и рабочие выходные. По этим датам система исчисляет
        сроки по ст. 191/193 ГК РФ. Ежегодно пополняется администратором.
      </p>

      <CalendarForm onSaved={load} />

      <div className="mt-4">
        {state.kind === "loading" && (
          <p className="text-muted" role="status">
            Загрузка календаря…
          </p>
        )}
        {state.kind === "error" && (
          <div
            role="alert"
            className="rounded-[var(--radius-l)] border border-border bg-surface p-4"
          >
            <p className="text-overdue-fg">{state.message}</p>
            <button type="button" onClick={() => void load()} className={`mt-3 ${btnPrimary}`}>
              Повторить
            </button>
          </div>
        )}
        {state.kind === "ready" && (
          <>
            {years.length > 0 && (
              <div className="mb-3 flex items-center gap-2">
                <label htmlFor="cal-year" className="text-[13px] text-muted">
                  Год
                </label>
                <select
                  id="cal-year"
                  value={activeYear}
                  onChange={(e) => setYear(e.target.value)}
                  className="rounded-[var(--radius-m)] border border-border bg-surface px-2 py-1 text-[14px]"
                >
                  {years.map((y) => (
                    <option key={y} value={y}>
                      {y}
                    </option>
                  ))}
                </select>
                <span className="text-[13px] text-muted">дней: {shown.length}</span>
              </div>
            )}
            <CalendarTable items={shown} onChanged={load} />
          </>
        )}
      </div>
    </section>
  );
}

function CalendarForm({ onSaved }: { onSaved: () => Promise<void> }) {
  const [date, setDate] = useState("");
  const [type, setType] = useState<CalendarDayType>("holiday");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setBusy(true);
    const res = await createCalendarDay({ date, type, note: note.trim() });
    setBusy(false);
    if (res.ok) {
      setDate("");
      setNote("");
      setType("holiday");
      await onSaved();
      return;
    }
    setError(res.message);
  };

  return (
    <form
      onSubmit={onSubmit}
      aria-labelledby="cal-form-title"
      className="rounded-[var(--radius-l)] border border-border bg-surface p-4"
    >
      <h3 id="cal-form-title" className="mb-3 text-[14px] font-bold text-ink">
        Добавить день
      </h3>
      <div className="grid gap-3 sm:grid-cols-3">
        <div>
          <label htmlFor="cal-date" className="mb-1 block text-[13px] text-muted">
            Дата
          </label>
          <input
            id="cal-date"
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            className={`num ${inputCls}`}
            required
          />
        </div>
        <div>
          <label htmlFor="cal-type" className="mb-1 block text-[13px] text-muted">
            Тип дня
          </label>
          <select
            id="cal-type"
            value={type}
            onChange={(e) => setType(e.target.value as CalendarDayType)}
            className={inputCls}
          >
            <option value="holiday">{DAY_TYPE_LABELS.holiday}</option>
            <option value="working">{DAY_TYPE_LABELS.working}</option>
          </select>
        </div>
        <div>
          <label htmlFor="cal-note" className="mb-1 block text-[13px] text-muted">
            Примечание
          </label>
          <input
            id="cal-note"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Постановление №…"
            className={inputCls}
          />
        </div>
      </div>
      {error && (
        <p role="alert" className="mt-3 text-[13px] text-overdue-fg">
          {error}
        </p>
      )}
      <div className="mt-4">
        <button type="submit" disabled={busy} className={btnPrimary}>
          {busy ? "Добавление…" : "Добавить"}
        </button>
      </div>
    </form>
  );
}

function CalendarTable({
  items,
  onChanged,
}: {
  items: CalendarDay[];
  onChanged: () => Promise<void>;
}) {
  if (items.length === 0) {
    return <p className="text-muted">В календаре пока нет дней за этот год.</p>;
  }
  return (
    <div className="overflow-x-auto rounded-[var(--radius-l)] border border-border bg-surface">
      <table className="w-full border-collapse text-[14px]">
        <caption className="sr-only">Дни производственного календаря</caption>
        <thead>
          <tr className="border-b border-border text-left text-[12px] text-muted">
            <th scope="col" className="px-4 py-2.5 font-bold">
              Дата
            </th>
            <th scope="col" className="px-4 py-2.5 font-bold">
              Тип
            </th>
            <th scope="col" className="px-4 py-2.5 font-bold">
              Примечание
            </th>
            <th scope="col" className="px-4 py-2.5 font-bold">
              Действия
            </th>
          </tr>
        </thead>
        <tbody>
          {items.map((d) => (
            <CalendarRow key={d.id} day={d} onChanged={onChanged} />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function CalendarRow({ day, onChanged }: { day: CalendarDay; onChanged: () => Promise<void> }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const doDelete = async () => {
    setBusy(true);
    setError(null);
    const res = await deleteCalendarDay(day.id);
    setBusy(false);
    if (res.ok) {
      await onChanged();
      return;
    }
    setError(res.message);
  };

  return (
    <tr className="border-b border-border-soft align-top last:border-b-0">
      <td className="num px-4 py-3 text-ink">{formatDate(day.date)}</td>
      <td className="px-4 py-3">
        <DayTypeStamp type={day.type} />
      </td>
      <td className="px-4 py-3 text-muted">{day.note ?? "—"}</td>
      <td className="px-4 py-3">
        <button
          type="button"
          disabled={busy}
          className={btnSecondary}
          onClick={() => void doDelete()}
        >
          Удалить
        </button>
        {error && (
          <p role="alert" className="mt-1.5 text-[12px] text-overdue-fg">
            {error}
          </p>
        )}
      </td>
    </tr>
  );
}

// ── Ключевая ставка ЦБ ──────────────────────────────────────────────────────────

export function KeyRatesSection() {
  const [state, setState] = useState<Load<KeyRate>>({ kind: "loading" });

  const load = useCallback(async () => {
    setState({ kind: "loading" });
    const res = await fetchKeyRates();
    setState(
      res.ok ? { kind: "ready", items: res.value } : { kind: "error", message: res.message },
    );
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  // Свежие ставки сверху (текущая — первой).
  const rows = state.kind === "ready" ? [...state.items].reverse() : [];

  return (
    <section aria-labelledby="rate-title" className="mt-10">
      <h2 id="rate-title" className="mb-1 text-[16px] font-bold text-ink">
        Ключевая ставка ЦБ
      </h2>
      <p className="mb-3 text-[13px] text-muted">
        Ставка действует с указанной даты до следующей записи. Используется для расчёта процентов по
        ст. 395 ГК РФ. Обновляется администратором после решений Совета директоров ЦБ.
      </p>

      <KeyRateForm onSaved={load} />

      <div className="mt-4">
        {state.kind === "loading" && (
          <p className="text-muted" role="status">
            Загрузка ставок…
          </p>
        )}
        {state.kind === "error" && (
          <div
            role="alert"
            className="rounded-[var(--radius-l)] border border-border bg-surface p-4"
          >
            <p className="text-overdue-fg">{state.message}</p>
            <button type="button" onClick={() => void load()} className={`mt-3 ${btnPrimary}`}>
              Повторить
            </button>
          </div>
        )}
        {state.kind === "ready" && <KeyRatesTable items={rows} onChanged={load} />}
      </div>
    </section>
  );
}

function KeyRateForm({ onSaved }: { onSaved: () => Promise<void> }) {
  const [from, setFrom] = useState("");
  const [percent, setPercent] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    const rate = parsePercentToHundredths(percent);
    if (rate === null) {
      setError("Ставка — процент, например «16,5» (до двух знаков после запятой).");
      return;
    }
    setBusy(true);
    const res = await createKeyRate({ from, rate, note: note.trim() });
    setBusy(false);
    if (res.ok) {
      setFrom("");
      setPercent("");
      setNote("");
      await onSaved();
      return;
    }
    setError(res.message);
  };

  return (
    <form
      onSubmit={onSubmit}
      aria-labelledby="rate-form-title"
      className="rounded-[var(--radius-l)] border border-border bg-surface p-4"
    >
      <h3 id="rate-form-title" className="mb-3 text-[14px] font-bold text-ink">
        Добавить ставку
      </h3>
      <div className="grid gap-3 sm:grid-cols-3">
        <div>
          <label htmlFor="rate-from" className="mb-1 block text-[13px] text-muted">
            Действует с
          </label>
          <input
            id="rate-from"
            type="date"
            value={from}
            onChange={(e) => setFrom(e.target.value)}
            className={`num ${inputCls}`}
            required
          />
        </div>
        <div>
          <label htmlFor="rate-percent" className="mb-1 block text-[13px] text-muted">
            Ставка, % годовых
          </label>
          <input
            id="rate-percent"
            value={percent}
            onChange={(e) => setPercent(e.target.value)}
            inputMode="decimal"
            placeholder="16,5"
            className={`num ${inputCls}`}
            required
          />
        </div>
        <div>
          <label htmlFor="rate-note" className="mb-1 block text-[13px] text-muted">
            Примечание
          </label>
          <input
            id="rate-note"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Решение ЦБ от…"
            className={inputCls}
          />
        </div>
      </div>
      {error && (
        <p role="alert" className="mt-3 text-[13px] text-overdue-fg">
          {error}
        </p>
      )}
      <div className="mt-4">
        <button type="submit" disabled={busy} className={btnPrimary}>
          {busy ? "Добавление…" : "Добавить"}
        </button>
      </div>
    </form>
  );
}

function KeyRatesTable({ items, onChanged }: { items: KeyRate[]; onChanged: () => Promise<void> }) {
  if (items.length === 0) {
    return <p className="text-muted">Ставки пока не заданы.</p>;
  }
  return (
    <div className="overflow-x-auto rounded-[var(--radius-l)] border border-border bg-surface">
      <table className="w-full border-collapse text-[14px]">
        <caption className="sr-only">Ключевая ставка ЦБ по периодам</caption>
        <thead>
          <tr className="border-b border-border text-left text-[12px] text-muted">
            <th scope="col" className="px-4 py-2.5 font-bold">
              Действует с
            </th>
            <th scope="col" className="px-4 py-2.5 font-bold">
              Ставка
            </th>
            <th scope="col" className="px-4 py-2.5 font-bold">
              Примечание
            </th>
            <th scope="col" className="px-4 py-2.5 font-bold">
              Действия
            </th>
          </tr>
        </thead>
        <tbody>
          {items.map((r) => (
            <KeyRateRow key={r.id} rate={r} onChanged={onChanged} />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function KeyRateRow({ rate, onChanged }: { rate: KeyRate; onChanged: () => Promise<void> }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(formatHundredthsAsPercent(rate.rate));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const save = async () => {
    setError(null);
    const value = parsePercentToHundredths(draft);
    if (value === null) {
      setError("Укажите ставку процентом, например «16,5».");
      return;
    }
    setBusy(true);
    const res = await updateKeyRate(rate.id, { rate: value });
    setBusy(false);
    if (res.ok) {
      setEditing(false);
      await onChanged();
      return;
    }
    setError(res.message);
  };

  const doDelete = async () => {
    setBusy(true);
    setError(null);
    const res = await deleteKeyRate(rate.id);
    setBusy(false);
    if (res.ok) {
      await onChanged();
      return;
    }
    setError(res.message);
  };

  return (
    <tr className="border-b border-border-soft align-top last:border-b-0">
      <td className="num px-4 py-3 text-ink">{formatDate(rate.from)}</td>
      <td className="num px-4 py-3 text-ink">
        {editing ? (
          <input
            aria-label={`Ставка с ${rate.from}`}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            inputMode="decimal"
            className="w-24 rounded-[var(--radius-m)] border border-border bg-surface px-2 py-1 text-[14px]"
          />
        ) : (
          `${formatHundredthsAsPercent(rate.rate)} %`
        )}
      </td>
      <td className="px-4 py-3 text-muted">{rate.note ?? "—"}</td>
      <td className="px-4 py-3">
        <div className="flex flex-wrap items-center gap-2">
          {editing ? (
            <>
              <button
                type="button"
                disabled={busy}
                className={btnPrimary}
                onClick={() => void save()}
              >
                Сохранить
              </button>
              <button
                type="button"
                disabled={busy}
                className={btnSecondary}
                onClick={() => {
                  setDraft(formatHundredthsAsPercent(rate.rate));
                  setEditing(false);
                }}
              >
                Отмена
              </button>
            </>
          ) : (
            <>
              <button
                type="button"
                disabled={busy}
                className={btnSecondary}
                onClick={() => setEditing(true)}
              >
                Изменить
              </button>
              <button
                type="button"
                disabled={busy}
                className={btnSecondary}
                onClick={() => void doDelete()}
              >
                Удалить
              </button>
            </>
          )}
        </div>
        {error && (
          <p role="alert" className="mt-1.5 text-[12px] text-overdue-fg">
            {error}
          </p>
        )}
      </td>
    </tr>
  );
}
